// src/pages/Farm.js
// Farm marketplace page 
// Composes: FarmHeader, FarmProductGrid, useFarmProducts, useFarmWishlist.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, setDoc, increment } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../contexts/ToastContext';
import ErrorModal from '../components/ErrorModal';
import useGeolocation from '../hooks/useGeolocation';
import useFarmProducts from '../hooks/useFarmProducts';
import useFarmWishlist from '../hooks/useFarmWishlist';
import useDebounced from '../hooks/useDebounced';
import FarmHeader from '../components/farm/FarmHeader';
import FarmProductGrid from '../components/farm/FarmProductGrid';
import { errorDev } from '../utils/logger.js';

const MAX_WISH_COUNT_LISTENERS = 20;

export default function Farm() {
  const { t } = useTranslation('farmMarketplace');
  const navigate = useNavigate();
  const toastCtx = useToast();
  const { user } = useAuth();

  // -- UI state --
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [nearbyActive, setNearbyActive] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const debouncedSearch = useDebounced(searchTerm, 350);

  // -- Geolocation (runs on mount; result cached by the hook) --
  const geo = useGeolocation();

  // Safe toast wrapper
  const showToast = useCallback(
    (opts = {}) => {
      try {
        if (typeof toastCtx !== 'function') return;

        const { type = 'info', message = '', ...rest } = opts;
        if (!message) return;

        if (typeof toastCtx[type] === 'function') {
          toastCtx[type](message, rest);
        } else {
          toastCtx(message, { type, ...rest });
        }
      } catch (err) {
        // no-op
      errorDev("Operation failed:", err);
      }
    },
    [toastCtx]
  );

  // Handle Nearby toggle with location validation
  const handleNearbyToggle = useCallback(
    (setter) => {
      const nextVal = typeof setter === 'function' ? setter(nearbyActive) : setter;
      if (nextVal) {
        if (geo.error) {
          setLocationError(geo.error);
          return;
        }
        if (geo.loading) {
          showToast({ type: 'info', message: t('locationRequired') });
          return;
        }
        if (!geo.coords) {
          setLocationError(t('locationUnavailable'));
          return;
        }
        setSortOrder('closest');
      }
      setNearbyActive(nextVal);
    },
    [nearbyActive, geo, t, showToast]
  );

  useEffect(() => {
    if (nearbyActive && sortOrder !== 'closest') {
      setNearbyActive(false)
    }
  }, [sortOrder, nearbyActive])

  // -- Products hook --
  const {
    products: allProducts,
    visible: products,
    hasMore,
    loadMore,
    loading,
    error: loadError,
    retry,
  } = useFarmProducts({
    searchTerm: debouncedSearch,
    category,
    sortOrder,
    userCoords: geo.coords,
    nearbyActive: nearbyActive && !!geo.coords,
  });

  // -- Wishlist --
  const { isWished, toggleWishlist: rawToggle } = useFarmWishlist();

  // Wish counts (product-level, public): productId -> number
  const [wishCounts, setWishCounts] = useState({});
  const wishCountUnsubs = useRef({});

  const wishCountListenerIds = useMemo(
    () => products
      .filter((product) => product?.wishCount == null)
      .map((product) => product.id)
      .filter(Boolean)
      .slice(0, MAX_WISH_COUNT_LISTENERS),
    [products]
  );

  useEffect(() => {
    const currentIds = new Set(wishCountListenerIds);
    // Unsubscribe removed products
    Object.keys(wishCountUnsubs.current).forEach((id) => {
      if (!currentIds.has(id)) {
        wishCountUnsubs.current[id]();
        delete wishCountUnsubs.current[id];
      }
    });
    // Subscribe new products
    wishCountListenerIds.forEach((productId) => {
      if (wishCountUnsubs.current[productId]) return;
      const ref = doc(db, 'products', productId);
      wishCountUnsubs.current[productId] = onSnapshot(
        ref,
        (snap) => {
          const data = snap.data();
          if (data && data.wishCount != null) {
            setWishCounts((prev) => ({ ...prev, [productId]: data.wishCount }));
          }
        },
        () => { } // silent on error
      );
    });

  }, [wishCountListenerIds]);
  useEffect(() => {
    return () => {
      Object.values(wishCountUnsubs.current).forEach((u) => {
        try { u(); } catch (err) { errorDev("Operation failed:", err);}
      });
      wishCountUnsubs.current = {};
    };
  }, []);

  // Wishlist toggle handler with count increment/decrement
  const handleToggleWishlist = useCallback(
    async (product) => {
      if (!user) {
        navigate('/login');
        return;
      }
      try {
        const nowWished = await rawToggle(product);
        // Update public wish count on the product doc (best-effort)
        try {
          await setDoc(
            doc(db, 'products', product.id),
            { wishCount: increment(nowWished ? 1 : -1) },
            { merge: true }
          );
        } catch (err) {
          // non-critical
        errorDev("Operation failed:", err);
        }
        showToast({
          type: 'success',
          message: nowWished ? t('wishlistAdded') : t('wishlistRemoved'),
        });
      } catch (err) {
        if (err && err.message === 'not-authenticated') {
          navigate('/login');
        } else {
          showToast({ type: 'error', message: t('wishlistFailed') });
        }
      }
    },
    [rawToggle, navigate, t, showToast]
  );

  // Show load error toast
  useEffect(() => {
    if (loadError) {
      showToast({ type: 'error', message: t('loadFailed') });
    }
  }, [loadError, showToast, t]);

  // Extract unique category ids from loaded products (for dynamic categories)
  const productCategories = useMemo(
    () => [...new Set(allProducts.filter((p) => p.category).map((p) => p.category))],
    [allProducts]
  );


  // -- Render --
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 mobile-nav-safe-padding">
      <FarmHeader
        category={category}
        setCategory={setCategory}
        nearbyActive={nearbyActive}
        setNearbyActive={handleNearbyToggle}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
        productCategories={productCategories}
      />

      <main data-farm-grid className="max-w-7xl mx-auto px-3 sm:px-4 pt-5 pb-6">
        {/* Sort controls */}
        <div className="flex items-center justify-end mb-4 pb-3 border-b border-gray-200/80 dark:border-neutral-800">
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="text-xs border border-gray-300 dark:border-neutral-700 rounded-lg px-2 py-1 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-green-500"
            aria-label={t('filters.sortBy')}
          >
            <option value="newest">{t('filters.newest')}</option>
            <option value="priceLow">{t('filters.priceLow')}</option>
            <option value="priceHigh">{t('filters.priceHigh')}</option>
            {geo.coords && <option value="closest">{t('filters.closest')}</option>}
          </select>
        </div>

        {/* Loading state */}
        {loading && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="orrith-loader orrith-loader-md orrith-loader-primary" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {t('loading', { ns: 'common' })}
            </p>
          </div>
        )}

        {/* Error state with retry */}
        {loadError && products.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-24 text-center px-4">
            <p className="text-gray-600 dark:text-gray-300 mb-3">{t('loadFailed')}</p>
            <button
              onClick={retry}
              className="bg-green-700 hover:bg-green-800 text-white px-5 py-2 rounded-full text-sm"
            >
              {t('retry')}
            </button>
          </div>
        )}

        {/* Product grid */}
        {(!loading || products.length > 0) && !loadError && (
          <FarmProductGrid
            products={products}
            isWished={isWished}
            wishCounts={wishCounts}
            onToggleWishlist={handleToggleWishlist}
            hasMore={hasMore}
            loadMore={loadMore}
            loading={loading}
          />
        )}
      </main>

      {/* Location error dialog */}
      {locationError && (
        <ErrorModal
          title={t('nearby')}
          message={locationError}
          onClose={() => {
            setLocationError(null);
            setNearbyActive(false);
          }}
          actionLabel={t('close', { ns: 'common' })}
        />
      )}

    </div>
  );
}
